/* ============================================================
   SLICK STORE — SHARED CART ENGINE
   File: assets/ss-cart.js
   ============================================================
   Single source of truth for ALL cart behavior:
     - Fetch /cart.js and build a cart map (variantId -> qty)
     - Check if a variant is "maxed" in cart vs inventory
     - Add to cart via /cart/add.js
     - Render Dawn cart drawer / cart notification
     - Update cart count bubble in header
     - Fire cart:updated / cart:refresh events
     - Cross-tab sync via storage event (ss-cart-ts key)
 
   Loaded from layout/theme.liquid so EVERY page has it.
 
   Replaces inline duplicates in:
     - snippets/card-product.liquid  (window.ssGridAddToCart)
     - sections/related-products.liquid  (relatedAddToCart)
     - snippets/ss-product-info.liquid  (dawnAtcAdd partial)
 
   The product page keeps its own ATC wrapper that calls
   SlickCart.add() under the hood, because it has extra
   responsibilities: gift wrap, Buy Now, sticky bar, variant
   change handling, quantity input.
   ============================================================ */
 
(function () {
  'use strict';
 
  /* ------------------------------------------------------------
     Internal state
     ------------------------------------------------------------
     cartQtyMap: { "variantId": quantityInCart }
     Updated after every /cart.js fetch.
  ------------------------------------------------------------ */
  var cartQtyMap = {};
  var ready = false;
  var STORAGE_TS_KEY = 'ss-cart-ts';
 
  /* ------------------------------------------------------------
     getDawnCartElement()
     ------------------------------------------------------------
     Dawn theme uses one of two custom elements for cart UI:
     <cart-drawer> if the theme is set to "drawer" mode,
     <cart-notification> if set to "notification" mode.
     We support both.
  ------------------------------------------------------------ */
  function getDawnCartElement() {
    return document.querySelector('cart-drawer') ||
           document.querySelector('cart-notification');
  }
 
  /* ------------------------------------------------------------
     getDawnSectionsToRender(cartEl)
     ------------------------------------------------------------
     Dawn's Section Rendering API needs us to tell it which
     sections to re-render after a cart change. The cart-drawer
     and cart-notification elements know their own answer to
     this. If neither is present, fall back to the bare minimum.
  ------------------------------------------------------------ */
  function getDawnSectionsToRender(cartEl) {
    if (!cartEl) return [];
    try {
      if (typeof cartEl.getSectionsToRender === 'function') {
        return cartEl.getSectionsToRender().map(function (s) { return s.id; });
      }
    } catch (e) {}
    return ['cart-icon-bubble', 'cart-live-region-text'];
  }
 
  /* ------------------------------------------------------------
     rebuildCartMap(cart)
     ------------------------------------------------------------
     Given a cart object from /cart.js, rebuild the cart map.
     Handles items with same variant_id appearing in multiple
     lines (rare but possible) by summing quantities.
  ------------------------------------------------------------ */
  function rebuildCartMap(cart) {
    cartQtyMap = {};
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        var key = String(item.variant_id);
        cartQtyMap[key] = (cartQtyMap[key] || 0) + item.quantity;
      });
    }
    ready = true;
  }
 
  /* ------------------------------------------------------------
     getCartQty(variantId)
     ------------------------------------------------------------
     How many of this variant are currently in cart?
  ------------------------------------------------------------ */
  function getCartQty(variantId) {
    return cartQtyMap[String(variantId)] || 0;
  }
 
  /* ------------------------------------------------------------
     isMaxed(btn)
     ------------------------------------------------------------
     Reads inventory-related data attributes off the Add button
     and decides if "Max in cart" should apply.
 
     Logic priority (matches the rules in original card-product):
       1. If not available -> NOT max, it's sold-out
       2. If untracked inventory -> never max
       3. If continue-selling policy -> never max
       4. If tracked + deny policy + inCart >= inventory -> MAX
 
     Expected data attrs on the button:
       data-variant-id, data-inventory-quantity,
       data-inventory-management, data-inventory-policy,
       data-available
  ------------------------------------------------------------ */
  function isMaxed(btn) {
    if (!btn) return false;
    var variantId  = btn.getAttribute('data-variant-id');
    var qty        = parseInt(btn.getAttribute('data-inventory-quantity') || '0', 10);
    var management = btn.getAttribute('data-inventory-management');
    var policy     = btn.getAttribute('data-inventory-policy');
    var available  = btn.getAttribute('data-available') !== 'false';
    var inCart     = getCartQty(variantId);
 
    if (!available)             return false;
    if (!management)            return false;
    if (policy === 'continue')  return false;
 
    return management === 'shopify' && qty > 0 && inCart >= qty;
  }
 
  /* ------------------------------------------------------------
     hardMax(inventory, variantId)
     ------------------------------------------------------------
     Returns the maximum quantity that can be added to cart
     for this variant. Used by the product page quantity input.
     Subtracts what's already in cart from total inventory.
 
     inventory shape: { qty, management, policy, available }
  ------------------------------------------------------------ */
  function hardMax(inventory, variantId) {
    if (!inventory) return 0;
    if (inventory.policy === 'continue') return 9999;
    if (inventory.management === 'shopify' && inventory.qty > 0) {
      var inCart = getCartQty(variantId);
      return Math.max(0, inventory.qty - inCart);
    }
    return 9999;
  }
 
  /* ------------------------------------------------------------
     syncGridButtons()
     ------------------------------------------------------------
     Loop over every .ss-card-add button on the page and set
     its visual state based on the current cart map:
       - "Sold Out" if unavailable
       - "Max" if maxed in cart
       - "Add" otherwise
 
     Skips buttons currently mid-add (have .is-loading class).
  ------------------------------------------------------------ */
  function syncGridButtons() {
    document.querySelectorAll('.ss-card-add[data-variant-id]').forEach(function (btn) {
      if (btn.classList.contains('is-loading')) return;
 
      var label = btn.querySelector('span');
      var available = btn.getAttribute('data-available') !== 'false';
 
      if (!available || btn.classList.contains('ss-card-add--off')) {
        btn.disabled = true;
        if (label) label.textContent = 'Sold Out';
        return;
      }
 
      if (isMaxed(btn)) {
        btn.disabled = true;
        btn.classList.add('ss-card-add--max');
        if (label) label.textContent = 'Max';
      } else {
        btn.disabled = false;
        btn.classList.remove('ss-card-add--max');
        if (label) {
          var txt = label.textContent.trim();
          if (txt === 'Max' || txt === 'Sold Out' || txt === '✓ Added' || txt === 'Error') {
            label.textContent = 'Add';
          }
        }
      }
    });
  }
 
  /* ------------------------------------------------------------
     updateCartBubble(cart)
     ------------------------------------------------------------
     Updates the header cart count bubble.
     If the bubble doesn't exist yet (empty cart on first load),
     create one.
  ------------------------------------------------------------ */
  function updateCartBubble(cart) {
    if (!cart) return;
    var count = cart.item_count || 0;
 
    document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(function (el) {
      el.textContent = count;
    });
 
    document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
      el.classList.remove('hidden');
    });
 
    var cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && !cartIcon.querySelector('.cart-count-bubble') && count > 0) {
      var bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML =
        '<span aria-hidden="true">' + count + '</span>' +
        '<span class="visually-hidden">' + count + ' items</span>';
      cartIcon.appendChild(bubble);
    }
  }
 
  /* ------------------------------------------------------------
     fetchCart(callback)
     ------------------------------------------------------------
     Fetch /cart.js, rebuild the cart map, sync UI.
     Returns a Promise. Callback (if provided) is called with
     the cart object on success, or null on failure.
  ------------------------------------------------------------ */
  function fetchCart(callback) {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        rebuildCartMap(cart);
        updateCartBubble(cart);
        syncGridButtons();
        if (callback) callback(cart);
        return cart;
      })
      .catch(function (err) {
        ready = true;
        if (callback) callback(null);
        return null;
      });
  }
 
  /* ------------------------------------------------------------
     signalCrossTab()
     ------------------------------------------------------------
     Writes a timestamp to localStorage to notify other open
     tabs that the cart changed. Other tabs listen for 'storage'
     events on this key and re-fetch their own cart state.
  ------------------------------------------------------------ */
  function signalCrossTab() {
    try {
      localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
    } catch (e) {
      // localStorage unavailable, skip cross-tab sync
    }
  }
 
  /* ------------------------------------------------------------
     add(variantId, quantity, options)
     ------------------------------------------------------------
     The main entry point for ALL cart additions.
     options = {
       button: optional DOM element to manage loading state,
       onSuccess: callback(response) on success,
       onError: callback(error) on failure,
       openDrawer: boolean (default true) - whether to open
         the Dawn cart drawer/notification on success
     }
  ------------------------------------------------------------ */
  function add(variantId, quantity, options) {
    options = options || {};
    quantity = quantity || 1;
 
    var btn = options.button || null;
    var cartEl = getDawnCartElement();
    var sections = getDawnSectionsToRender(cartEl);
 
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    if (sections.length && options.openDrawer !== false) {
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }
 
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData,
      credentials: 'same-origin'
    })
    .then(function (response) { return response.json(); })
    .then(function (response) {
      if (response.status && response.status !== 200) {
        throw response;
      }
 
      // Open Dawn cart UI if requested
      if (options.openDrawer !== false && cartEl && typeof cartEl.renderContents === 'function') {
        try {
          if (btn && typeof cartEl.setActiveElement === 'function') {
            cartEl.setActiveElement(btn);
          }
          cartEl.renderContents(response);
        } catch (e) {
          console.warn('Cart drawer render failed (item was still added):', e);
        }
      }
 
      // Refresh cart state and notify everyone
      return fetchCart().then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'ss-cart-add', cart: cart, variantId: variantId }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { cart: cart }
        }));
        signalCrossTab();
 
        if (options.onSuccess) options.onSuccess(response);
        return response;
      });
    })
    .catch(function (err) {
      console.error('SlickCart.add failed:', err);
      if (options.onError) options.onError(err);
      // Even on failure, refresh state so UI doesn't show stale "Adding..."
      fetchCart();
      throw err;
    });
  }
 
  /* ------------------------------------------------------------
     Public API
  ------------------------------------------------------------ */
  window.SlickCart = {
    fetch:           fetchCart,
    add:             add,
    getQty:          getCartQty,
    isMaxed:         isMaxed,
    hardMax:         hardMax,
    syncGridButtons: syncGridButtons,
    isReady:         function () { return ready; }
  };
 
  /* ------------------------------------------------------------
     Boot
  ------------------------------------------------------------ */
  function boot() {
    fetchCart();
 
    // Cross-tab sync
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_TS_KEY) {
        fetchCart();
      }
    });
 
    // Listen for cart changes from OTHER sources (e.g. cart drawer
    // quantity edits via Dawn's own cart-items component).
    document.addEventListener('cart:updated', function (e) {
      var source = e && e.detail && e.detail.source;
      if (source === 'ss-cart-add') return;  // we already refreshed
      fetchCart();
    });
 
    // Re-sync buttons when new ones appear (e.g. related products
    // load after page render).
    var debounceTimer = null;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(syncGridButtons, 200);
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree:   true
    });
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
